import { useEffect, useRef, useSyncExternalStore, type ReactNode } from "react";
import type { ApiClient } from "../api/client";
import { ApiError } from "../api/errors";
import type { Dashboard } from "../api/types";
import { Icon, type IconName } from "../components/Icon";
import { LegalLinks } from "../components/LegalLinks";
import { ToastProvider } from "../components/toast";
import { Button, Dot, ErrorNote, Loading } from "../components/ui";
import type { QueryCache } from "../data/cache";
import { DataProvider, useApi, useQuery, useRealtime, useStreamStatus } from "../data/hooks";
import { useDashboard, useNotificationSummary } from "../data/queries";
import { usePushResync } from "../pwa/push";
import { UsageGauge } from "../components/UsageGauge";
import { pct } from "../lib/format";
import { runnerHealth } from "../lib/status";
import { updateStore } from "../pwa/register";
import { Link, useLocation } from "./router";
import { resolveRoute } from "./routes";

export function App({ api, cache }: { api: ApiClient; cache: QueryCache }) {
  return (
    <DataProvider api={api} cache={cache}>
      <ToastProvider>
        <AuthGate>
          <Shell />
        </AuthGate>
      </ToastProvider>
    </DataProvider>
  );
}

function AuthGate({ children }: { children: ReactNode }) {
  const api = useApi();
  const me = useQuery("me", () => api.me(), { staleMs: 5 * 60_000 });
  if (me.error instanceof ApiError && me.error.code === "unauthorized") return <SignIn />;
  if (me.data) return <>{children}</>;
  if (me.error) return <div className="center-screen"><ErrorNote error={me.error} onRetry={() => me.refetch()} /></div>;
  return <div className="center-screen"><Loading label="Opening ContentOps…" /></div>;
}

function SignIn() {
  return (
    <main className="signin">
      <img src="/icons/icon.svg" alt="" width={72} height={72} />
      <h1>ContentOps</h1>
      <p className="muted">Review, publish and schedule to your own channels — from anywhere.</p>
      <a className="btn btn-primary btn-lg" href="/auth/google">
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden><path fill="currentColor" d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.3zM12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22zM6.4 14c-.2-.6-.3-1.3-.3-2s.1-1.4.3-2V7.4H3.1a10 10 0 0 0 0 9.2zM12 5.9c1.5 0 2.8.5 3.8 1.5l2.9-2.9A10 10 0 0 0 3.1 7.4L6.4 10c.8-2.4 3-4.1 5.6-4.1z" /></svg>
        <span>Sign in with Google</span>
      </a>
      <p className="muted small">Only allow-listed owner accounts can sign in.</p>
      <LegalLinks describe className="signin-legal" />
    </main>
  );
}

const NAV: { to: string; label: string; icon: IconName; badge?: (d: Dashboard) => number }[] = [
  { to: "/", label: "Home", icon: "home", badge: (d) => d.needs_attention.total },
  { to: "/new", label: "New", icon: "plus" },
  // Learnings live under the Studio (a 7-slot phone tab bar has no room for an 8th); their count joins the Studio badge.
  { to: "/studio", label: "Studio", icon: "studio", badge: (d) => d.plans_awaiting_selection + d.awaiting_review + d.learnings_pending },
  { to: "/publish", label: "Publish", icon: "publish", badge: (d) => d.needs_attention.items.filter((i) => i.kind.startsWith("publication_") || i.kind === "publishing_paused").length },
  { to: "/inbox", label: "Inbox", icon: "inbox", badge: (d) => d.comments_needing_attention },
  { to: "/analytics", label: "Analytics", icon: "chart" },
  { to: "/settings", label: "Settings", icon: "settings" },
];

function Shell() {
  useRealtime();
  usePushResync();
  const { path } = useLocation();
  const route = resolveRoute(path);
  const mainRef = useRef<HTMLElement>(null);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    mainRef.current?.focus({ preventScroll: true }); // announce the new screen to screen readers
  }, [path]);
  useEffect(() => {
    // Deep links like /settings#runner: scroll once the section has rendered its data.
    const id = window.location.hash.slice(1);
    if (!id) return;
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView?.({ block: "start" }), 400);
    return () => clearTimeout(t);
  }, [path]);
  useEffect(() => { document.title = route.title ? `${route.title} · ContentOps` : "ContentOps"; }, [route.title]);
  return (
    <div className="shell">
      <a className="skip-link" href="#main">Skip to content</a>
      <TopBar />
      <Nav path={path} />
      <main id="main" ref={mainRef} tabIndex={-1} className={`main ${route.wide ? "main-wide" : ""}`}>{route.element}</main>
      <UpdateBanner />
    </div>
  );
}

function isActive(to: string, path: string) {
  return to === "/" ? path === "/" : path === to || path.startsWith(`${to}/`) || (to === "/studio" && (path.startsWith("/review/") || path === "/learnings"));
}

function Nav({ path }: { path: string }) {
  const dash = useDashboard().data;
  return (
    <nav className="nav" aria-label="Main">
      <Link to="/" className="nav-brand" aria-label="ContentOps home">
        <img src="/icons/icon.svg" alt="" width={32} height={32} /> <span>ContentOps</span>
      </Link>
      <ul>
        {NAV.map((n) => {
          const count = dash && n.badge ? n.badge(dash) : 0;
          const active = isActive(n.to, path);
          return (
            <li key={n.to}>
              <Link to={n.to} className={`nav-item ${n.to === "/new" ? "nav-new" : ""}`} aria-current={active ? "page" : undefined}>
                <span className="nav-icon"><Icon name={n.icon} />{count > 0 && <span className="nav-badge" aria-label={`${count} need attention`}>{count > 99 ? "99+" : count}</span>}</span>
                <span className="nav-label">{n.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
      {/* Desktop sidebar only (hidden in the phone tab bar; phones reach them from Settings). */}
      <LegalLinks className="nav-legal" />
    </nav>
  );
}

function TopBar() {
  const dash = useDashboard().data;
  const stream = useStreamStatus();
  const runner = dash?.runner;
  const health = runnerHealth(runner?.last_seen_at);
  const usage = runner?.usage;
  const five = usage?.five_hour_pct ?? null;
  const seven = usage?.seven_day_pct ?? null;
  return (
    <header className="topbar">
      <Link to="/" className="topbar-brand" aria-label="ContentOps home"><img src="/icons/icon.svg" alt="" width={28} height={28} /><span>ContentOps</span></Link>
      <div className="topbar-status">
        {stream === "reconnecting" && <span className="live-note" role="status">Reconnecting…</span>}
        <Bell />
        <Link to="/settings#runner" className="runner-pill" aria-label={`${health.label}. Subscription usage: 5-hour ${pct(five)}, 7-day ${pct(seven)}${usage?.limited ? ", usage-limited" : ""}`}>
          <Dot tone={dash ? health.tone : "neutral"} label={health.label} />
          <span className="runner-name">Studio</span>
          <UsageGauge label="5h" value={five} />
          <UsageGauge label="7d" value={seven} />
        </Link>
      </div>
    </header>
  );
}

/** The notifications bell with the unread count (GET /api/notifications → unread_count; SSE "notifications" keeps it live). */
function Bell() {
  const unread = useNotificationSummary().data?.unread_count ?? 0;
  return (
    <Link to="/notifications" className="bell" aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}>
      <Icon name="bell" />
      {unread > 0 && <span className="nav-badge" aria-hidden>{unread > 99 ? "99+" : unread}</span>}
    </Link>
  );
}

function UpdateBanner() {
  const waiting = useSyncExternalStore(updateStore.subscribe, updateStore.get);
  if (!waiting) return null;
  return (
    <div className="update-banner" role="status">
      <Icon name="refresh" />
      <span>A new version of ContentOps is ready.</span>
      <Button size="sm" variant="primary" onClick={() => updateStore.apply()}>Reload</Button>
    </div>
  );
}
